"use client";

import type { Editor } from "@tiptap/react";
import { useCallback, useRef, useState } from "react";
import { readRenderedColor } from "./readRenderedColor";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import ColorPicker from "@/components/ui/color-picker";
import { Palette } from "lucide-react";
import { usePopoverRefocus } from "./refocusEditor";

interface ColorPickerControlProps {
    editor: Editor;
    textColor: string;
}

export function ColorPickerControl({
    editor,
    textColor,
}: ColorPickerControlProps) {
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const popoverRefocus = usePopoverRefocus(editor);
    const [openColor, setOpenColor] = useState<string>("");
    const colorPickerStoredSelectionRef = useRef<{
        from: number;
        to: number;
    } | null>(null);

    const applyColorToSelection = useCallback(
        (color: string) => {
            const stored = colorPickerStoredSelectionRef.current;
            if (stored) {
                editor
                    .chain()
                    .setTextSelection(stored)
                    .setColor(color)
                    .run();
                // NOTE: keep the stored selection set so subsequent drags on
                // the same picker session continue to target the same range.
                // It's cleared when the popover closes.
            } else {
                editor.chain().setColor(color).run();
            }
        },
        [editor]
    );

    const closeAndClearStoredSelection = useCallback(() => {
        colorPickerStoredSelectionRef.current = null;
        setColorPickerOpen(false);
    }, []);

    const resetColorForSelection = useCallback(() => {
        const stored = colorPickerStoredSelectionRef.current;
        if (stored) {
            editor
                .chain()
                .setTextSelection(stored)
                .unsetColor()
                .run();
            colorPickerStoredSelectionRef.current = null;
        } else {
            editor.chain().unsetColor().run();
        }
    }, [editor]);

    return (
        <Popover
            open={colorPickerOpen}
            onOpenChange={(open) => {
                if (open) {
                    setOpenColor(
                        readRenderedColor(
                            editor,
                            colorPickerStoredSelectionRef.current
                        )
                    );
                }
                setColorPickerOpen(open);
                if (!open) {
                    colorPickerStoredSelectionRef.current = null;
                }
            }}
        >
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 p-1"
                    aria-label="Text color"
                    onPointerDownCapture={() => {
                        if (!colorPickerOpen) {
                            const { from, to } = editor.state.selection;
                            if (from !== to) {
                                colorPickerStoredSelectionRef.current = {
                                    from,
                                    to,
                                };
                            }
                        }
                    }}
                >
                    <div className="relative flex items-center justify-center size-full">
                        <Palette className="size-4" />
                        {textColor ? (
                            <span
                                className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-3 h-0.5 rounded-full border border-gray-300"
                                style={{ backgroundColor: textColor }}
                            />
                        ) : null}
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-auto p-4"
                align="start"
                {...popoverRefocus}
            >
                <div className="flex flex-col gap-2">
                    <div className="text-xs font-medium text-muted-foreground">
                        Text color
                    </div>
                    <ColorPicker
                        key={openColor || textColor || "default"}
                        mode="nice-colors"
                        initialColor={openColor || textColor || "#000000"}
                        onPaletteColorSelect={(color) => {
                            applyColorToSelection(color);
                            closeAndClearStoredSelection();
                        }}
                        onColorChange={(color) =>
                            applyColorToSelection(color)
                        }
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={resetColorForSelection}
                    >
                        Reset (inherit)
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
